import { Box, MenuItem, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Button, Typography } from "@platform/design-system";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";
import {
  ALL_SERVICE_TYPES,
  makeDraftEntry,
  parseValidEntry,
  ServiceWaitingPeriodsField,
  toDraftEntries,
} from "./ServiceWaitingPeriodsField";

import type { ICellEditorParams } from "ag-grid-community";
import type { ServiceWaitingPeriod } from "@platform/plan-grid-core";
import type { JsonValue } from "../utils/popupCustomText";

export interface ServiceWaitingPeriodsCellEditorParams {
  initialPeriods: ServiceWaitingPeriod[];
  /** Header label shown at the top of the popover. */
  fieldLabel: string;
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams<unknown, ServiceWaitingPeriod[]> &
  ServiceWaitingPeriodsCellEditorParams;

// Matches the width every other Quotes-tab popup uses (TypedAmountCellEditor,
// FieldPopoverEditor, …). Unlike the fixed-height list editors used for other
// LOCs (AgeReductionScheduleCellEditor, GuaranteeIssueCellEditor), Dental's
// own popups size to their content, so this one does too.
const POPOVER_WIDTH = 440;

/** Whether the Edit Benefit side is showing the waiting-period list, or is
 * set to "None" (no waiting periods for this plan, nothing else to edit). */
type BenefitMode = "none" | "waitingPeriods";

/**
 * AG Grid popup cell editor for Dental's Service Waiting Periods. Edit
 * Benefit starts with a Type dropdown — "None" (saves an empty list, nothing
 * else shown) or "Waiting Periods" (a list of `service type` + `months`
 * entries, with its own Add/remove controls).
 */
export const ServiceWaitingPeriodsCellEditor = forwardRef<unknown, Props>(
  function ServiceWaitingPeriodsCellEditor(props, ref) {
    const {
      initialPeriods,
      fieldLabel,
      initialCustomText,
      onCustomTextChange,
      api,
      column,
      node,
      stopEditing,
      value,
    } = props;

    const [entries, setEntries] = useState(() =>
      toDraftEntries(initialPeriods),
    );
    const [showErrors, setShowErrors] = useState(false);
    const [benefitMode, setBenefitMode] = useState<BenefitMode>(
      initialPeriods.length > 0 ? "waitingPeriods" : "none",
    );

    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;

    // The cell's actual pre-edit AG Grid value — distinct from `initialPeriods`,
    // which normalizes an unanswered cell to a freshly-allocated `[]` for the
    // Edit Benefit list UI. Recommitting that fresh array instead of `value`
    // would read to AG Grid as a real change (`[] !== undefined`), silently
    // persisting "no waiting periods" as the plan's benefit value on every
    // custom-text save of an otherwise-unanswered cell.
    const lastValueRef = useRef<unknown>(value);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const canAddService = entries.length < ALL_SERVICE_TYPES.length;

    const addEntry = (): void => {
      const selectedTypes = new Set(entries.map((entry) => entry.type));
      const nextType = ALL_SERVICE_TYPES.find(
        (type) => !selectedTypes.has(type),
      );
      if (!nextType) return;

      setEntries((prev) => [...prev, makeDraftEntry(nextType, "1")]);
    };

    // Switching to None clears any drafted entries — there's nothing left to
    // resume editing if the user switches back to Waiting Periods without
    // saving.
    const handleBenefitModeChange = (mode: BenefitMode): void => {
      setBenefitMode(mode);
      if (mode === "none") setEntries([]);
    };

    const updateType = (
      index: number,
      type: ServiceWaitingPeriod["type"],
    ): void => {
      setEntries((prev) =>
        prev.map((entry, i) => (i === index ? { ...entry, type } : entry)),
      );
    };

    const updateMonths = (index: number, months: string): void => {
      setEntries((prev) =>
        prev.map((entry, i) => (i === index ? { ...entry, months } : entry)),
      );
    };

    const removeEntry = (index: number): void => {
      setEntries((prev) => prev.filter((_, i) => i !== index));
    };

    const persistValue = (finalValue: ServiceWaitingPeriod[]): void => {
      lastValueRef.current = finalValue;

      if (initialCustomText) onCustomTextChange?.(null);

      stopEditing(true);

      setTimeout(() => {
        if (column && node) {
          node.setDataValue(column, finalValue);
        }
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = value;
        customText.saveCustomText(value);
        return;
      }

      if (benefitMode === "none") {
        persistValue([]);
        return;
      }

      const parsedEntries = entries.map(parseValidEntry);
      if (parsedEntries.includes(null)) {
        setShowErrors(true);
        return;
      }

      const finalValue = parsedEntries.filter(
        (entry): entry is ServiceWaitingPeriod => entry !== null,
      );

      persistValue(finalValue);
    };

    const handleCancel = (): void => {
      lastValueRef.current = value;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: POPOVER_WIDTH,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" color="textSecondary">
            {fieldLabel}
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {!editingCustomText && (
            <TextField
              select
              size="small"
              label="Type"
              value={benefitMode}
              onChange={(e) =>
                handleBenefitModeChange(e.target.value as BenefitMode)
              }
              fullWidth
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="waitingPeriods">Waiting Periods</MenuItem>
            </TextField>
          )}

          {!editingCustomText &&
            benefitMode === "waitingPeriods" &&
            entries.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No service waiting periods. Click Add Waiting Periods to add one
                or select None.
              </Typography>
            )}

          {!editingCustomText &&
            benefitMode === "waitingPeriods" &&
            entries.length > 0 && (
              <ServiceWaitingPeriodsField
                entries={entries}
                showErrors={showErrors}
                onUpdateType={updateType}
                onUpdateMonths={updateMonths}
                onRemove={removeEntry}
              />
            )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          {!editingCustomText && benefitMode === "waitingPeriods" && (
            <Button
              size="sm"
              variant="tertiary"
              startIcon={<AddIcon />}
              onClick={addEntry}
              disabled={!canAddService}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Waiting Periods
            </Button>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>

            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);
